import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { INSURANCE_DOCUMENT_LIST_QUERY, INSURANCE_DOCUMENT_LIST_TEMPLATES_QUERY } from '../queries';
import { useHeaderLinks } from 'providers/AreasProvider';
import InsuranceDocumentListHeaderLinks from './InsuranceDocumentListHeaderLinks';
import ListBrowser, { CollectionType, getListBrowserStore } from '@ts-private/list-browser';
import {
  DOCUMENT_STATES_QUERY,
  FINANCING_TYPES_QUERY,
  INSURANCE_TYPES_QUERY,
  VISIBILITY_LEVELS_QUERY
} from 'features/insuranceDocument/queries/insuranceDocumentQueries';
import { OFFER_TYPES_QUERY } from 'features/insuranceDocument/edit/general/generalData/queries';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { Card } from '@totalsoft/rocket-ui';
import { useApolloFetching } from '@ts-private/list-browser-apollo-extensions';
import InsuranceDocumentContainer from './InsuranceDocumentContainer';
import ExportModalComponent from 'features/common/components/export/ExportModalComponent';
import { useApolloClient } from '@apollo/client';
import { DELETE_INSURANCE_DOCUMENT_LIST_TEMPLATE, SAVE_INSURANCE_DOCUMENT_LIST_TEMPLATE } from '../mutations';

export const insuranceDocumentsStore = getListBrowserStore({
  name: 'insuranceDocuments-browser',
  filterDefinitions: [
    {
      name: 'insuranceTypeId',
      caption: 'Offer.List.Filter.InsuranceType',
      type: 'select',
      underlyingType: 'number'
    },
    {
      name: 'dateFrom',
      caption: 'Offer.List.Filter.DateFrom',
      type: 'date'
    },
    {
      name: 'dateTo',
      caption: 'Offer.List.Filter.DateTo',
      type: 'date'
    },
    {
      name: 'documentStateId',
      caption: 'Offer.List.Filter.DocumentState',
      type: 'select',
      underlyingType: 'number'
    },
    {
      name: 'documentNumber',
      caption: 'Offer.List.Filter.DocumentNumber',
      type: 'integer'
    },
    {
      name: 'clientName',
      caption: 'Offer.List.Filter.ClientName',
      type: 'text'
    },
    {
      name: 'uniqueID',
      caption: 'Offer.List.Filter.UniqueId',
      type: 'text'
    },
    {
      name: 'userName',
      caption: 'Offer.List.Filter.CreatedBy',
      type: 'text'
    },
    {
      name: 'issuedByName',
      caption: 'Offer.List.Filter.IssuedBy',
      type: 'text'
    },
    {
      name: 'insuranceDocumentNumber',
      caption: 'Offer.List.Filter.InsuranceDocumentNumber',
      type: 'text'
    },
    {
      name: 'financingType',
      caption: 'Offer.List.Filter.FinancingType',
      type: 'select',
      underlyingType: 'text'
    },
    {
      name: 'chassisNumber',
      caption: 'Offer.List.Filter.ChassisNumber',
      type: 'text'
    },
    {
      name: 'offerTypeId',
      caption: 'Offer.List.Filter.OfferType',
      type: 'select',
      underlyingType: 'number'
    },
    {
      name: 'visibilityLevelId',
      caption: 'Offer.List.Filter.VisibilityLevel',
      type: 'select',
      underlyingType: 'number',
      initialValue: 1,
      isClearable: 0
    }
  ],
  orderByFields: [
    {
      caption: 'Offer.List.Field.DocumentNumber',
      value: 'DocumentNumber'
    },
    {
      caption: 'Offer.List.Field.DocumentDate',
      value: 'DocumentDate'
    }
  ],
  pager: {
    orderBy: 'DocumentDate',
    direction: 'desc'
  },
  fields: [
    {
      name: 'assetImage',
      caption: 'Offer.List.Field.AssetImage'
    },
    {
      name: 'insuranceTypeName',
      caption: 'Offer.List.Field.InsuranceType'
    },
    {
      name: 'documentNumber',
      caption: 'Offer.List.Field.DocumentNumber'
    },
    {
      name: 'chassisNumber',
      caption: 'Offer.List.Field.ChassisNumber'
    },
    {
      name: 'issuedByName',
      caption: 'Offer.List.Field.IssuedBy'
    },
    {
      name: 'offerTypeName',
      caption: 'Offer.List.Field.OfferType'
    },
    {
      name: 'documentStateName',
      caption: 'Offer.List.Field.DocumentState'
    },
    {
      name: 'userName',
      caption: 'Offer.List.Field.CreatedBy'
    },
    {
      name: 'documentDate',
      caption: 'Offer.List.Field.DocumentDate'
    },
    {
      name: 'clientName',
      caption: 'Offer.List.Field.ClientName'
    },
    {
      name: 'vehicleName',
      caption: 'Offer.List.Field.Vehicle'
    }
  ]
});

const insuranceDocumentsFetchingOptions = {
  queryOptions: { query: INSURANCE_DOCUMENT_LIST_QUERY, name: 'insuranceDocuments', fetchPolicy: 'network-only' },
  collectionsOptions: [
    {
      queryOptions: { query: INSURANCE_TYPES_QUERY },
      name: 'insuranceTypes',
      identifier: 'insuranceTypeId',
      collectionType: CollectionType.Filter,
      valueKey: 'id',
      labelKey: 'name'
    },
    {
      queryOptions: { query: DOCUMENT_STATES_QUERY },
      name: 'documentStates',
      identifier: 'documentStateId',
      collectionType: CollectionType.Filter,
      valueKey: 'id',
      labelKey: 'name'
    },
    {
      queryOptions: { query: FINANCING_TYPES_QUERY },
      name: 'financingTypes',
      identifier: 'financingType',
      collectionType: CollectionType.Filter,
      valueKey: 'name',
      labelKey: 'name'
    },
    {
      queryOptions: { query: OFFER_TYPES_QUERY },
      name: 'offerTypes',
      identifier: 'offerTypeId',
      collectionType: CollectionType.Filter,
      valueKey: 'id',
      labelKey: 'name'
    },
    {
      queryOptions: { query: VISIBILITY_LEVELS_QUERY },
      name: 'visibilityLevels',
      identifier: 'visibilityLevelId',
      collectionType: CollectionType.Filter,
      valueKey: 'id',
      labelKey: 'name'
    }
  ]
};

const InsuranceDocumentListBrowser = () => {
  const apolloClient = useApolloClient();
  const [, setHeaderLinks] = useHeaderLinks();

  useApolloFetching(insuranceDocumentsStore, insuranceDocumentsFetchingOptions);

  useLayoutEffect(() => {
    setHeaderLinks(<InsuranceDocumentListHeaderLinks />);
  }, [setHeaderLinks]);

  const handleLoadTemplates = useCallback(async () => {
    const templatesData = await apolloClient.query({ query: INSURANCE_DOCUMENT_LIST_TEMPLATES_QUERY, fetchPolicy: 'network-only' });
    if (templatesData?.data?.insuranceDocumentListTemplates) {
      return templatesData?.data?.insuranceDocumentListTemplates.map(({ templateId, templateName, isDefault, filters, fields }) => ({
        id: templateId,
        name: templateName,
        isDefault,
        filters: filters,
        visibleFields: fields
      }));
    }
    return [];
  }, [apolloClient]);

  const handleSaveTemplate = useCallback(
    async template => {
      const input = {
        templateId: template.id,
        templateName: template.name,
        isDefault: template.isDefault,
        filters: template.filters,
        fields: template.visibleFields
      };
      const templateSavedData = await apolloClient.mutate({ mutation: SAVE_INSURANCE_DOCUMENT_LIST_TEMPLATE, variables: { input } });
      return templateSavedData.data.saveInsuranceDocumentListTemplate;
    },
    [apolloClient]
  );

  const handleDeleteTemplate = useCallback(
    async id => {
      await apolloClient.mutate({ mutation: DELETE_INSURANCE_DOCUMENT_LIST_TEMPLATE, variables: { input: { templateId: id } } });
    },
    [apolloClient]
  );

  const templateOptions = useMemo(
    () => ({
      load: handleLoadTemplates,
      onSave: handleSaveTemplate,
      onDelete: handleDeleteTemplate
    }),
    [handleLoadTemplates, handleSaveTemplate, handleDeleteTemplate]
  );

  return (
    <Card icon={ListAltIcon}>
      <ListBrowser
        store={insuranceDocumentsStore}
        ItemComponent={InsuranceDocumentContainer}
        withFilterSelection
        withFieldSelection
        templateOptions={templateOptions}
      />
      <ExportModalComponent store={insuranceDocumentsStore} options={insuranceDocumentsFetchingOptions} />
    </Card>
  );
};

export default InsuranceDocumentListBrowser;
